import React, {
  FunctionComponent,
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  CSSProperties,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { SearchFilterDropdown } from '../../molecules/searchFilterDropdown';
import { SearchFilterSelect } from '../../molecules/searchFilterSelect';
import type { Props as ComponentProps, Occupation, OccupationDetail } from './types';
import {
  FULL_TIME_WORKING_PERIOD_ID,
  FULL_TIME_LABEL,
  SIDEBAR_PREFERENCE_CONDITION_NAMES,
  SALARY_TYPES,
  SALARY_RANGE_OPTIONS,
  withSalaryRangeDisabled,
  filterSalaryTypes,
} from './types';
import { scrollToElementById } from '../../../libs/scrollUtils';
import type { SearchHistories, SearchCondition } from '../../../types/recruitments';

type Props = ComponentProps & {
  q: string;
  fetchList: string;
  fetchId: string;
  smallListStyle: CSSProperties;
  bigListStyle: CSSProperties;
  fetchIds: (event: MouseEvent<HTMLLIElement>) => void;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleChangeQ: (event: ChangeEvent<HTMLInputElement>) => void;
  handleBlurQ: () => void;
  handleClickBtn: () => void;
  handleChangeList: (event: MouseEvent<HTMLLIElement>) => void;
  handleSearch: () => void;
  handleClickSubmit: () => void;
  fetchDetails: (o: Occupation) => OccupationDetail[];
  checkPrefectureArea: (key: string) => boolean;
  checkPrefectureAreaSp: (key: string) => boolean;
  handleScrollTo: (name: string) => void;
  handleClearAll?: () => void;
  searchHistories?: SearchHistories;
  handleApplySearchHistory?: (condition: SearchCondition) => void;
  handleSaveConditions?: (title: string) => void;
  saveConditions?: SearchCondition[];
  handleDeleteSaveConditions?: (conditionId: number | string) => void;
};

export const Presentation: FunctionComponent<Props> = ({
  target_employment_types,
  target_occupations,
  target_occupation_details,
  target_prefecture_details,
  q,
  recruitment_employment_types,
  occupations,
  side_prefectures,
  all_prefectures,
  detail_prefectures,
  occupation_details,
  search_occupation_details,
  occupationDetailsOccupationIds,
  prefectureDetails,
  isPc,
  isSp,
  isPage,
  popupStyle,
  modalStyle,
  toggleListStyle,
  recruitmentsCount,
  fetchList,
  fetchId,
  fetchIds,
  handleSearch,
  handleClickSubmit,
  handleChangeList,
  handleFetchOccupationDetails,
  handleFetchPrefectureDetails,
  openList,
  handlePopup,
  handleModal,
  handleChange,
  handleChangeQ,
  handleBlurQ,
  handleClickBtn,
  fetchDetails,
  checkPrefectureArea,
  checkPrefectureAreaSp,
  smallListStyle,
  bigListStyle,
  handleScrollTo,
  handleClearAll,
  handleClearEmploymentTypes,
  handleSelectAllEmploymentTypes,
  handleClearOccupations,
  handleSelectAllOccupations,
  handleClearLocations,
  handleSelectAllLocations,
  target_preference_conditions,
  target_working_periods,
  preference_condition_categories,
  working_period_ids,
  salary_type_ids,
  handleClearPreferenceConditions,
  handleSelectAllPreferenceConditions,
  target_salary_type,
  target_salary_min,
  target_salary_max,
  handleChangeSalary,
  handleClearAreas,
  handleClearPrefectureDetails,
  searchHistories,
  handleApplySearchHistory,
  handleSaveConditions,
  saveConditions,
  handleDeleteSaveConditions,
}) => {
  const [modalView, setModalView] = useState('main');
  const [locationView, setLocationView] = useState('main');
  const [saveName, setSaveName] = useState('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyPanelOrigin, setHistoryPanelOrigin] = useState<'section' | 'modal' | null>(null);
  const [historySectionPopoverStyle, setHistorySectionPopoverStyle] = useState<CSSProperties | null>(null);
  const historyAnchorSectionRef = useRef<HTMLElement>(null);
  const [occupationFilter, setOccupationFilter] = useState('');
  const [prefectureFilter, setPrefectureFilter] = useState('');
  const [preferenceFilter, setPreferenceFilter] = useState('');
  const isModalActive = modalStyle?.opacity === '1';

  useEffect(() => {
    if (!historyModalOpen) return;

    const handleClickOutsideHistory = (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.js-search-history')) return;

      setHistoryModalOpen(false);
      setHistoryPanelOrigin(null);
    };

    document.addEventListener('mousedown', handleClickOutsideHistory);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideHistory);
    };
  }, [historyModalOpen]);

  useLayoutEffect(() => {
    if (!historyModalOpen || historyPanelOrigin !== 'section') {
      setHistorySectionPopoverStyle(null);
      return;
    }

    const updatePosition = () => {
      const el = historyAnchorSectionRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHistorySectionPopoverStyle({
        position: 'fixed',
        top: r.top + 40,
        right: window.innerWidth - r.right,
        width: r.width,
        left: 'auto',
        zIndex: 5,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [historyModalOpen, historyPanelOrigin]);

  // フィルタリングされた職種リストを生成
  const getFilteredOccupations = () => {
    if (!occupationFilter.trim()) {
      return occupations;
    }

    const filterText = occupationFilter.toLowerCase();

    return occupations?.filter((o) => {
      // 親カテゴリ名がフィルター文言を含むか
      const parentMatches = o.name.toLowerCase().includes(filterText);
      if (parentMatches) {
        return true;
      }

      // 子カテゴリのいずれかがフィルター文言を含むか
      const details = fetchDetails(o);
      const childMatches = details?.some((od) =>
        od.name.toLowerCase().includes(filterText)
      );
      return childMatches;
    });
  };

  // フィルタリングされた子カテゴリを取得
  const getFilteredDetails = (o: Occupation) => {
    const details = fetchDetails(o);
    if (!occupationFilter.trim()) {
      return details;
    }

    const filterText = occupationFilter.toLowerCase();

    // 親カテゴリ名がマッチする場合は全ての子カテゴリを表示
    if (o.name.toLowerCase().includes(filterText)) {
      return details;
    }

    // そうでなければ、マッチする子カテゴリのみ表示
    return details?.filter((od) =>
      od.name.toLowerCase().includes(filterText)
    );
  };

  // フィルタリングされた都道府県リストを生成
  const getFilteredPrefectures = () => {
    if (!prefectureFilter.trim()) {
      return all_prefectures;
    }

    const filterText = prefectureFilter.toLowerCase();

    return all_prefectures?.filter((p) =>
      p.name.toLowerCase().includes(filterText)
    );
  };

  // 都道府県へのスクロール
  const handleScrollToPrefecture = (name: string) => {
    const element = document.getElementById(`prefecture_${name}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const closeAndResetModal = () => {
    handlePopup();
    handleModal();
    setTimeout(() => {
      setModalView('main');
      setLocationView('main');
      setSaveName('');
      setOccupationFilter('');
      setPrefectureFilter('');
      setPreferenceFilter('');
    }, 300);
  };

  const handleSearchClick = () => {
    handleClickSubmit();
    closeAndResetModal();
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;

    event.preventDefault();
    handleSearch();
  };

  const handleConfirmKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.currentTarget.blur();
  };

  const handleSaveConditionClick = () => {
    handleSaveConditions?.(saveName);
    setModalView('main');
    setSaveName('');
  };

  const handleAddConditionsClick = () => {
    setModalView('main');
    setLocationView('main');
  };

  const handleApplyHistoryClick = (condition: SearchCondition) => {
    if (!handleApplySearchHistory) return;

    handleApplySearchHistory(condition);
    const shouldCloseModal = historyPanelOrigin === 'modal';

    setHistoryModalOpen(false);
    setHistoryPanelOrigin(null);

    if (shouldCloseModal) {
      closeAndResetModal();
    }
  };

  const selectedOccupationNames = () => {
    const selected_parent_occupations = (occupations || [])
      .filter((o) => (target_occupations || []).includes(String(o.id)))
      .map((o) => o.name);

    const selected_child_occupations = (occupation_details || [])
      .flatMap((od) => od.occupation_details || [])
      .filter((od) => (target_occupation_details || []).includes(String(od.id)))
      .map((od) => od.name);

    return [...selected_parent_occupations, ...selected_child_occupations];
  };

  const selectedOccupationsText = () => selectedOccupationNames().join(' , ');

  const prefectureIdToName: Record<string, string> = {
    '1': '北海道',
    '2': '青森県',
    '3': '岩手県',
    '4': '宮城県',
    '5': '秋田県',
    '6': '山形県',
    '7': '福島県',
    '8': '茨城県',
    '9': '栃木県',
    '10': '群馬県',
    '11': '埼玉県',
    '12': '千葉県',
    '13': '東京都',
    '14': '神奈川県',
    '15': '新潟県',
    '16': '富山県',
    '17': '石川県',
    '18': '福井県',
    '19': '山梨県',
    '20': '長野県',
    '21': '岐阜県',
    '22': '静岡県',
    '23': '愛知県',
    '24': '三重県',
    '25': '滋賀県',
    '26': '京都府',
    '27': '大阪府',
    '28': '兵庫県',
    '29': '奈良県',
    '30': '和歌山県',
    '31': '鳥取県',
    '32': '島根県',
    '33': '岡山県',
    '34': '広島県',
    '35': '山口県',
    '36': '徳島県',
    '37': '香川県',
    '38': '愛媛県',
    '39': '高知県',
    '40': '福岡県',
    '41': '佐賀県',
    '42': '長崎県',
    '43': '熊本県',
    '44': '大分県',
    '45': '宮崎県',
    '46': '鹿児島県',
    '47': '沖縄県',
  };

  const selectedLocationNames = () => {
    const selectedAreas = detail_prefectures
      ? Object.keys(detail_prefectures).filter((area) => checkPrefectureArea(area))
      : [];
    const selectedAreaPrefectureIds = detail_prefectures
      ? selectedAreas.flatMap((area) =>
        (detail_prefectures[area] || []).map((prefecture) => String(prefecture.id))
      )
      : [];
    const selectedPrefectures = (target_prefecture_details || [])
      .filter((id) => !selectedAreaPrefectureIds.includes(String(id)))
      .map((id) => prefectureIdToName[id] || id);

    return [...selectedAreas, ...selectedPrefectures];
  };

  const selectedPrefecturesText = () => selectedLocationNames().join(' , ');

  const flatPreferenceConditions = (preference_condition_categories || []).flatMap(
    (category) => category.preference_conditions
  );

  const isFullTimeSelected = (target_working_periods || []).includes(FULL_TIME_WORKING_PERIOD_ID);

  // サイドバーのこだわり条件は一部抽出（正社員＋Figma指定の条件）のみ表示
  const sidebarPreferenceOptions = [
    // 会社ページでは正社員（雇用期間なし）の求人がある場合のみ表示
    ...(!working_period_ids || working_period_ids.includes(Number(FULL_TIME_WORKING_PERIOD_ID))
      ? [
        {
          value: FULL_TIME_WORKING_PERIOD_ID,
          label: FULL_TIME_LABEL,
          name: 'target_working_periods',
          selected: isFullTimeSelected,
        },
      ]
      : []),
    ...SIDEBAR_PREFERENCE_CONDITION_NAMES.flatMap((name) => {
      const condition = flatPreferenceConditions.find((pc) => pc.name === name);
      return condition ? [{ value: String(condition.id), label: condition.name }] : [];
    }),
  ];

  // トリガー表示はモーダルで選択した条件も含めた全選択条件
  const selectedPreferenceNames = () => {
    const names = flatPreferenceConditions
      .filter((pc) => (target_preference_conditions || []).includes(String(pc.id)))
      .map((pc) => pc.name);
    return isFullTimeSelected ? [FULL_TIME_LABEL, ...names] : names;
  };

  // フィルター文字列に一致する条件のみ残したカテゴリ一覧（空カテゴリは除外）
  const getFilteredPreferenceCategories = () => {
    const keyword = preferenceFilter.trim().toLowerCase();
    return (preference_condition_categories || [])
      .map((category) => ({
        ...category,
        preference_conditions: category.preference_conditions.filter((pc) =>
          pc.name.toLowerCase().includes(keyword)
        ),
      }))
      .filter((category) => category.preference_conditions.length > 0);
  };

  const salarySummaryText = () => {
    const type = SALARY_TYPES.find((t) => t.id === (target_salary_type || ''));
    if (!type) return '';
    const options = SALARY_RANGE_OPTIONS[type.id] || [];
    const rangeLabel = (value?: string) => options.find((o) => o.value === value)?.label || '指定なし';
    return `${type.label} ${rangeLabel(target_salary_min)} ~ ${rangeLabel(target_salary_max)}`;
  };

  // 検索条件保存ボタンの活性判定（いずれかの条件が選択されているか）
  const hasActiveConditions = () =>
    Boolean(q.trim()) ||
    Boolean(selectedPrefecturesText()) ||
    Boolean(selectedOccupationsText()) ||
    target_employment_types.length > 0 ||
    selectedPreferenceNames().length > 0 ||
    Boolean(target_salary_type);

  // 会社/ブランドページでは salary_type_ids（対象求人が持つ給与タイプ）に含まれるものだけ表示。
  // 未指定（求人検索ページ）なら全タイプ。空配列なら給与セクション自体を非表示にする。
  const availableSalaryTypes = filterSalaryTypes(salary_type_ids);
  const showSalarySection = Boolean(handleChangeSalary) && availableSalaryTypes.length > 0;

  // 給与セクション（サイドバー・詳細検索モーダル共通）
  // inModal: 縦に狭いモーダル内では下限・上限のパネルを上へはみ出させず、常に下・画面高に追従させる
  const renderSalaryFields = (inModal = false) => {
    if (!showSalarySection || !handleChangeSalary) return null;
    const salaryType = target_salary_type || '';
    const rangeOptions = SALARY_RANGE_OPTIONS[salaryType] || [];
    const rangeLabel = `${SALARY_TYPES.find((t) => t.id === salaryType)?.label || '年収'}範囲`;

    // 下限は「上限以下」、上限は「下限以上」しか選べない。逆側の値を跨ぐ選択肢を無効化する（同額は許可）
    const minRangeOptions = withSalaryRangeDisabled(rangeOptions, target_salary_max, 'min');
    const maxRangeOptions = withSalaryRangeDisabled(rangeOptions, target_salary_min, 'max');

    return (
      <div className="search-form__salary">
        <ul className="search-form__salary__types">
          {availableSalaryTypes.map((type) => (
            <li key={type.id}>
              <label className="search-form__salary__types__item">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  name="target_salary_type"
                  value={type.id}
                  checked={salaryType === type.id}
                  onChange={() =>
                    // 排他選択。選択中のタイプを再クリックすると解除し、切替時は範囲をリセットする
                    handleChangeSalary({
                      target_salary_type: salaryType === type.id ? '' : type.id,
                      target_salary_min: '',
                      target_salary_max: '',
                    })
                  }
                />
                <span className="checkbox-text">{type.label}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className='search-form__salary__range-wrapper'>
          <p className="search-form__salary__range-label">{rangeLabel}</p>
          <div className="search-form__salary__range">
            <SearchFilterSelect
              ariaLabel={`${rangeLabel}（下限）`}
              disabled={!salaryType}
              flip={!inModal}
              options={minRangeOptions}
              value={target_salary_min || ''}
              onChange={(value) => handleChangeSalary({ target_salary_min: value })}
            />
            <span className="search-form__salary__range__separator">~</span>
            <SearchFilterSelect
              ariaLabel={`${rangeLabel}（上限）`}
              disabled={!salaryType}
              flip={!inModal}
              options={maxRangeOptions}
              value={target_salary_max || ''}
              onChange={(value) => handleChangeSalary({ target_salary_max: value })}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderTitle = () => {
    if (modalView === 'location') {
      if (locationView === 'area') return 'エリア';
      if (locationView === 'prefecture') return '都道府県';
      return '勤務地';
    }
    switch (modalView) {
      case 'employment':
        return '雇用形態';
      case 'occupation':
        return '職種';
      case 'preference':
        return 'こだわり条件';
      case 'salary':
        return '給与';
      case 'save':
        return '検索条件を保存';
      default:
        return '詳細検索';
    }
  };

  const handleBack = () => {
    if (locationView !== 'main') {
      setLocationView('main');
    } else if (modalView !== 'main') {
      setModalView('main');
    }
  };

  const renderModalContent = () => {
    switch (modalView) {
      case 'main':
        return (
          <>
            {isPage !== 'recruitments' && (
            <div className="keyword">
              <p className='keyword__title'>キーワード</p>
              <input
                value={q}
                placeholder="27卒 古着"
                type="text"
                name="q"
                className="notbeforeunload"
                aria-label="27卒 古着"
                onChange={handleChangeQ}
                enterKeyHint="done"
                onKeyDown={handleConfirmKeyDown}
              />
            </div>
            )}
            <ul className="search-form__modal__inner__list first">
              <li onClick={() => setModalView('employment')}>
                <p>雇用形態</p>
                <span className={`data${target_employment_types.length === 0 ? ' empty' : ''}`}>{target_employment_types.length > 0 ? target_employment_types.join(' , ') : '未選択'}</span>
                <span className="material-symbols-outlined arrow">
                  chevron_right
                </span>
              </li>
              <li onClick={() => setModalView('occupation')}>
                <p>職種</p>
                <span className={`data${!selectedOccupationsText() ? ' empty' : ''}`}>{selectedOccupationsText() || '未選択'}</span>
                <span className="material-symbols-outlined arrow">
                  chevron_right
                </span>
              </li>
              <li onClick={() => setModalView('location')}>
                <p>勤務地</p>
                <span className={`data${!selectedPrefecturesText() ? ' empty' : ''}`}>{selectedPrefecturesText() || '未選択'}</span>
                <span className="material-symbols-outlined arrow">
                  chevron_right
                </span>
              </li>
              {(preference_condition_categories || []).length > 0 && (
                <li onClick={() => setModalView('preference')}>
                  <p>こだわり条件</p>
                  <span className={`data${selectedPreferenceNames().length === 0 ? ' empty' : ''}`}>{selectedPreferenceNames().length > 0 ? selectedPreferenceNames().join(' , ') : '未選択'}</span>
                  <span className="material-symbols-outlined arrow">
                    chevron_right
                  </span>
                </li>
              )}
              {showSalarySection && (
                <li onClick={() => setModalView('salary')}>
                  <p>給与</p>
                  <span className={`data${salarySummaryText() ? '' : ' empty'}`}>{salarySummaryText() || '未選択'}</span>
                  <span className="material-symbols-outlined arrow">
                    chevron_right
                  </span>
                </li>
              )}
            </ul>
            {isPage !== 'recruitments' && (
            <div className={`save ${hasActiveConditions() ? 'active' : ''}`}>
              <p className="search-form__modal__inner__save-title">保存済み検索条件</p>
              {saveConditions && saveConditions.length > 0 && (
                <ul className="search-form__modal__inner__save-list">
                  {saveConditions.map((condition) => (
                    <li
                      key={condition.id}
                      className="search-form__modal__inner__save-list__item"
                      onClick={() => {
                        if (handleApplySearchHistory) {
                          handleApplySearchHistory(condition);
                          closeAndResetModal();
                        }
                      }}
                    >
                      <div className="search-form__modal__inner__save-list__item__content">
                        <div className="search-form__modal__inner__save-list__item__title">
                          {condition.title || '保存条件'}
                        </div>
                        <div className="search-form__modal__inner__save-list__item__body">
                          {condition.body}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="search-form__modal__inner__save-list__item__delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (handleDeleteSaveConditions) {
                            handleDeleteSaveConditions(condition.id);
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M0.735395 12L0 11.2646L5.26461 6L0 0.735394L0.735395 0L6 5.26461L11.2646 0L12 0.735394L6.7354 6L12 11.2646L11.2646 12L6 6.73539L0.735395 12Z" fill="#19190F"></path></svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="search-form__modal__inner__save-button"
                onClick={() => {
                  if (hasActiveConditions()) {
                    setModalView('save');
                  }
                }}
              >
                <span className="plus">+</span>
                いまの検索条件を保存する
              </button>
            </div>
            )}
          </>
        );
      case 'employment':
        return (
          <ul className="search-form__modal__inner__list inbox">
            {recruitment_employment_types?.map((et) => (
              <li key={et} className={(target_employment_types?.includes(String(et)) ? 'active' : '')}>
                <label className='checkbox wide'>
                  {et}
                  <input
                    type="checkbox"
                    name="target_employment_types"
                    value={et}
                    onChange={handleChange}
                    checked={target_employment_types?.includes(String(et))}
                    className="checkbox-input"
                  />
                  {target_employment_types.includes(String(et)) &&
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="10"
                      viewBox="0 0 14 10"
                      fill="none"
                    >
                      <path
                        d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z"
                        fill="#19190F"
                      />
                    </svg>
                  }
                </label>
              </li>
            ))}
          </ul>
        );
      case 'preference':
        return (
          <>
            <div className="person__show__modal__inner__occupations">
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  placeholder="絞り込む"
                  className="filter-input"
                  value={preferenceFilter}
                  onChange={(e) => setPreferenceFilter(e.target.value)}
                />
              </div>

              <div className="filter-tags-wrapper">
                <ul className="filter-tags-wrapper__list">
                  {getFilteredPreferenceCategories().map((category) => (
                    <li key={category.id} data-id={category.id} className="filter-tags-wrapper__list--item">
                      <button
                        type="button"
                        onClick={() => scrollToElementById(`preference_category_${category.id}`)}
                        className="btn"
                      >
                        {category.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="person__show__modal__inner__occupations--list">
              {getFilteredPreferenceCategories().map((category) => (
                <ul key={category.id} className="occupation--box search-form__modal__inner__list preference" id={`preference_category_${category.id}`}>
                  <li>
                    <label className='checkbox wide heading'>{category.name}</label>
                  </li>
                  {category.preference_conditions.map((pc) => (
                    <li key={pc.id} className={(target_preference_conditions || []).includes(String(pc.id)) ? 'active' : ''}>
                      <label className='checkbox wide'>
                        {pc.name}
                        <input
                          type="checkbox"
                          name="target_preference_conditions"
                          value={pc.id}
                          onChange={handleChange}
                          checked={(target_preference_conditions || []).includes(String(pc.id))}
                          className="checkbox-input"
                        />
                        {(target_preference_conditions || []).includes(String(pc.id)) &&
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="10"
                            viewBox="0 0 14 10"
                            fill="none"
                          >
                            <path
                              d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z"
                              fill="#19190F"
                            />
                          </svg>
                        }
                      </label>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </>
        );
      case 'salary':
        return (
          <>
            {renderSalaryFields(true)}
          </>
        );
      case 'occupation':
        return (
          <>
            <div className="person__show__modal__inner__occupations">
              {/* Filter Input */}
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  placeholder="絞り込む"
                  className="filter-input"
                  value={occupationFilter}
                  onChange={(e) => setOccupationFilter(e.target.value)}
                />
              </div>

              {/* Filter Tags */}
              <div className="filter-tags-wrapper">
                <ul className="filter-tags-wrapper__list">
                  {getFilteredOccupations()?.map((o) => (
                    <li key={o.id} data-id={o.id} className="filter-tags-wrapper__list--item">
                      <button
                        type="button"
                        onClick={() => handleScrollTo(o.name)}
                        className="btn"
                      >
                        {o.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="person__show__modal__inner__occupations--list">
              {getFilteredOccupations()?.map((o) => (
                <ul key={o.id} className="occupation--box search-form__modal__inner__list">
                  {/* Parent Occupation */}
                  <li className={`${target_occupations?.includes(String(o.id)) && 'active'}`} id={`occupation_${o.name}`}>
                    <label className='checkbox wide heading'>
                      {o.name}
                      <input
                        type="checkbox"
                        name="target_occupations"
                        value={o.id}
                        onChange={handleChange}
                        checked={target_occupations?.includes(String(o.id))}
                        className="checkbox-input"
                      />
                      {target_occupations?.includes(String(o.id)) &&
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="10"
                          viewBox="0 0 14 10"
                          fill="none"
                        >
                          <path
                            d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z"
                            fill="#19190F"
                          />
                        </svg>
                      }
                    </label>
                  </li>
                  {/* Sub Occupations */}
                  {getFilteredDetails(o)?.map((od) => (
                    <li className={`${target_occupation_details?.includes(String(od.id)) && 'active'}`} key={od.id}>
                      <label className='checkbox wide'>
                        {od.name}
                        <input
                          type="checkbox"
                          name="target_occupation_details"
                          value={od.id}
                          onChange={handleChange}
                          checked={target_occupation_details?.includes(String(od.id))}
                          className="checkbox-input"
                        />
                        {target_occupation_details?.includes(String(od.id)) &&
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="10"
                            viewBox="0 0 14 10"
                            fill="none"
                          >
                            <path
                              d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z"
                              fill="#19190F"
                            />
                          </svg>
                        }
                      </label>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </>
        );
      case 'location':
        if (locationView === 'main') {
          return (
            <ul className="search-form__modal__inner__list inbox area">
              <li onClick={() => setLocationView('area')}>
                <p>エリア</p>
                <span className="material-symbols-outlined arrow">
                  chevron_right
                </span>
              </li>
              <li onClick={() => setLocationView('prefecture')}>
                <p>都道府県</p>
                <span className="material-symbols-outlined arrow">
                  chevron_right
                </span>
              </li>
            </ul>
          );
        }
        if (locationView === 'area') {
          return (
            <ul className="search-form__modal__inner__list inbox">
              {detail_prefectures &&
                Object.keys(detail_prefectures).map((area) => (
                  <li key={area} className={`occupation--box ${checkPrefectureArea(area) && 'active'}`}>
                    <label className="checkbox wide">
                      {area}
                      <input
                        type="checkbox"
                        name="target_prefectures"
                        value={area}
                        onChange={handleChange}
                        checked={checkPrefectureArea(area)}
                        className="checkbox-input"
                      />
                      {checkPrefectureArea(area) &&
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="10"
                          viewBox="0 0 14 10"
                          fill="none"
                        >
                          <path
                            d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z"
                            fill="#19190F"
                          />
                        </svg>
                      }
                    </label>
                  </li>
                ))}
            </ul>
          );
        }
        if (locationView === 'prefecture') {
          return (
            <>
              <div className="person__show__modal__inner__occupations">
                {/* Filter Input */}
                <div className="filter-input-wrapper">
                  <input
                    type="text"
                    placeholder="絞り込む"
                    className="filter-input"
                    value={prefectureFilter}
                    onChange={(e) => setPrefectureFilter(e.target.value)}
                  />
                </div>

                {/* Filter Tags */}
                <div className="filter-tags-wrapper">
                  <ul className="filter-tags-wrapper__list">
                    {side_prefectures?.map((p) => (
                      <li key={p.id} data-id={p.id} className="filter-tags-wrapper__list--item">
                        <button
                          type="button"
                          onClick={() => handleScrollToPrefecture(p.name)}
                          className="btn"
                        >
                          {p.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <ul className="search-form__modal__inner__list inbox">
                {getFilteredPrefectures()?.map((p) => (
                  <li key={p.id} className={`${target_prefecture_details?.includes(String(p.id)) && 'active'}`} id={`prefecture_${p.name}`}>
                    <label className="checkbox wide">
                      {p.name}
                      <input
                        type="checkbox"
                        name="target_prefecture_details"
                        value={p.id}
                        onChange={handleChange}
                        checked={target_prefecture_details?.includes(String(p.id))}
                        className="checkbox-input"
                      />
                      {target_prefecture_details?.includes(String(p.id)) &&
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="10"
                          viewBox="0 0 14 10"
                          fill="none"
                        >
                          <path
                            d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z"
                            fill="#19190F"
                          />
                        </svg>
                      }
                    </label>
                  </li>
                ))}
              </ul>
            </>
          );
        }
        return null;
      case 'save':
        return (
          <div className="search-form__modal__save">
            <p className="search-form__modal__save__description">検索条件の名前を追加して保存してください</p>
            <input
              type="text"
              value={saveName}
              placeholder="条件名"
              className="search-form__modal__save__input"
              onChange={(e) => setSaveName(e.target.value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const target_occupation_details_count = occupationDetailsOccupationIds.reduce((sum, i) => sum + (i.occupation_detail_id?.length ?? 0), 0);
  return (
    <>
      <section className="search-form__wrapper">
        <div className="search-form__inner">
          <section ref={historyAnchorSectionRef} className="search-form__inner__section sticky">
            <p className="search-form__inner__section__category--title heading">
              求人を絞り込む
              <span className="search-form__inner__section--btn title-section" onClick={handleModal}>
                詳細検索
              </span>
              {/* 検索履歴は項目選択だけでも検索が走るので期待する挙動でないため一旦コメントアウト */}
              {/* <span
                className="material-symbols-outlined js-search-history"
                onClick={() => {
                  setHistoryModalOpen((prev) => {
                    const next = !prev;
                    setHistoryPanelOrigin(next ? 'section' : null);
                    return next;
                  });
                }}
                style={{ cursor: 'pointer' }}
              >
                history
              </span> */}
            </p>
            {/* 上記同様、検索履歴を非表示にする関係でこちらも一旦コメントアウト */}
            {/* 検索履歴ポップアップ（サイドバーから開いたときのみ body へ Portal） */}
            {/* {historyModalOpen &&
              historyPanelOrigin === 'section' &&
              historySectionPopoverStyle &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  className="search-form__inner__section__history js-search-history"
                  style={historySectionPopoverStyle}
                >
                  <div className="search-form__inner__section__history__wrapper">
                    <div className="search-form__inner__section__history__title">
                      <p className="title">検索履歴から検索</p>
                    </div>
                    <div className="search-form__inner__section__history__content">
                      <ul className="search-form__inner__section__history__content__list">
                        {searchHistories?.conditions && searchHistories.conditions.length > 0 ? (
                          searchHistories.conditions.map((condition) => (
                            <li
                              className="search-form__inner__section__history__content__list__item"
                              key={condition.id}
                              onClick={() => handleApplyHistoryClick(condition)}
                              style={{ cursor: 'pointer' }}
                            >
                              <p>{condition.body}</p>
                            </li>
                          ))
                        ) : (
                          <li className="search-form__inner__section__history__content__list__item empty">
                            <p>検索履歴がありません</p>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>,
                document.body,
              )} */}
          </section>
          {isPage !== 'recruitments' && (
            <section className="search-form__inner__section margin">
              <p className="search-form__inner__section__category--title">キーワード検索</p>
              <div className="search-form__inner__section--box">
                <label>
                  <input
                    value={q}
                    placeholder="27卒 古着"
                    type="text"
                    name="q"
                    className="notbeforeunload"
                    aria-label="27卒 古着"
                    onChange={handleChangeQ}
                    onBlur={handleBlurQ}
                    onKeyDown={handleSearchKeyDown}
                  />
                  {isSp && <span className="search-form__inner__section--box--icon" onClick={handleClickBtn}></span>}
                </label>
              </div>
            </section>
          )}
          {saveConditions && saveConditions.length > 0 && (
            <section className="search-form__inner__section search-form__inner__section__saved">
              <p className="search-form__inner__section__category--title">保存済み検索条件</p>
              <ul className="search-form__inner__section__saved__list">
                {saveConditions.map((condition) => (
                  <li
                    key={condition.id}
                    className="search-form__inner__section__saved__list__item"
                    onClick={() => {
                      if (handleApplySearchHistory) {
                        handleApplySearchHistory(condition);
                      }
                    }}
                  >
                    <div className="search-form__inner__section__saved__list__item__content">
                      <p className="search-form__inner__section__saved__list__item__title">
                        {condition.title || '保存条件'}
                      </p>
                      <p className="search-form__inner__section__saved__list__item__body">
                        {condition.body}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="search-form__inner__section__saved__list__item__delete"
                      aria-label={`${condition.title || '保存条件'}を削除`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (handleDeleteSaveConditions) {
                          handleDeleteSaveConditions(condition.id);
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M0.735395 12L0 11.2646L5.26461 6L0 0.735394L0.735395 0L6 5.26461L11.2646 0L12 0.735394L6.7354 6L12 11.2646L11.2646 12L6 6.73539L0.735395 12Z" fill="#19190F"></path></svg>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="search-form__inner__section">
            <p className="search-form__inner__section__category--title">雇用形態</p>
            <SearchFilterDropdown
              name="target_employment_types"
              options={(recruitment_employment_types || []).map((et) => ({ value: String(et), label: String(et) }))}
              selectedValues={target_employment_types || []}
              handleChange={handleChange}
              onClearAll={handleClearEmploymentTypes}
              onSelectAll={handleSelectAllEmploymentTypes}
            />
          </section>
          <section className="search-form__inner__section">
            <p className="search-form__inner__section__category--title">
              職種
              <span
                className="search-form__inner__section--btn"
                onClick={() => {
                  handleModal();
                  setModalView('occupation');
                }}
              >
                さらに選択する
              </span>
            </p>
            <SearchFilterDropdown
              name="target_occupations"
              options={(occupations || []).map((o) => ({ value: String(o.id), label: o.name }))}
              selectedValues={target_occupations || []}
              handleChange={handleChange}
              onClearAll={handleClearOccupations}
              onSelectAll={handleSelectAllOccupations}
            />
          </section>
          <section className="search-form__inner__section">
            <p className="search-form__inner__section__category--title">
              勤務地
              <span
                className="search-form__inner__section--btn"
                onClick={() => {
                  handleModal();
                  setModalView('location');
                }}
              >
                さらに選択する
              </span>
            </p>
            <SearchFilterDropdown
              name="target_prefecture_details"
              options={(side_prefectures || []).map((p) => ({ value: String(p.id), label: p.name }))}
              selectedValues={target_prefecture_details || []}
              handleChange={handleChange}
              onClearAll={handleClearLocations}
              onSelectAll={handleSelectAllLocations}
            />
          </section>
          {sidebarPreferenceOptions.length > 0 && (
            <section className="search-form__inner__section">
              <p className="search-form__inner__section__category--title">
                こだわり条件
                <span
                  className="search-form__inner__section--btn"
                  onClick={() => {
                    handleModal();
                    setModalView('preference');
                  }}
                >
                  さらに選択する
                </span>
              </p>
              <SearchFilterDropdown
                name="target_preference_conditions"
                options={sidebarPreferenceOptions}
                selectedValues={target_preference_conditions || []}
                handleChange={handleChange}
                onClearAll={handleClearPreferenceConditions}
                onSelectAll={handleSelectAllPreferenceConditions}
              />
            </section>
          )}
          {showSalarySection && (
            <section className="search-form__inner__section">
              <p className="search-form__inner__section__category--title">給与</p>
              {renderSalaryFields()}
            </section>
          )}
        </div>
        {isPage !== 'recruitments' && (
          <>
            <div className="search-form--button">
              <div className="search-form--button--num">
                <p>該当求人数</p>
                <span>{recruitmentsCount}</span>件
              </div>
              <button onClick={handleClickBtn}>
                <input type="submit" value="この条件で検索する" />
              </button>
            </div>
            <div className="search-form--bottom">
              {handleClearAll && (
                <p className="clear" onClick={handleClearAll}>すべてクリア</p>
              )}
              <button className={`submit save ${hasActiveConditions() ? 'active' : ''}`} onClick={() => {
                if (hasActiveConditions()) {
                  handleModal();
                  setModalView('save');
                }
              }}>
                <span className="plus">+</span>
                検索条件を保存する
              </button>
            </div>
          </>
        )}
      </section>

      {typeof document !== 'undefined' && createPortal(
        <>
          <div className={`search-form__modal--bg ${isModalActive ? 'active' : ''}`} onClick={closeAndResetModal}></div>
          <div className={`search-form__modal ${isModalActive ? 'active' : ''}`}>
            <div className={`search-form__modal__wrapper ${modalView === 'save' && 'save'}`}>
              <div className="search-form__modal__title">
                {modalView !== 'main' && modalView !== 'save' ? (
                  <div className="mask" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M0.735395 12L0 11.2646L5.26461 6L0 0.735394L0.735395 0L6 5.26461L11.2646 0L12 0.735394L6.7354 6L12 11.2646L11.2646 12L6 6.73539L0.735395 12Z" fill="#19190F"></path></svg>
                  </div>
                ) : (
                  <div className="mask" onClick={closeAndResetModal}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M0.735395 12L0 11.2646L5.26461 6L0 0.735394L0.735395 0L6 5.26461L11.2646 0L12 0.735394L6.7354 6L12 11.2646L11.2646 12L6 6.73539L0.735395 12Z" fill="#19190F"></path></svg>
                  </div>
                )}
                <p className="title">{renderTitle()}</p>
                {/* 検索履歴は項目選択だけでも検索が走るので期待する挙動でないため一旦コメントアウト */}
                {/* <span
                  className="material-symbols-outlined js-search-history"
                  onClick={() => {
                    setHistoryModalOpen((prev) => {
                      const next = !prev;
                      setHistoryPanelOrigin(next ? 'modal' : null);
                      return next;
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  history
                </span> */}

                {/* 上記同様、検索履歴を非表示にする関係でこちらも一旦コメントアウト */}
                {/* {historyModalOpen && historyPanelOrigin === 'modal' && (
                  <div
                    className="search-form__inner__section__history modal js-search-history"
                    style={{ opacity: '1', visibility: 'visible' } as CSSProperties}
                  >
                    <div className="search-form__inner__section__history__wrapper">
                      <div className="search-form__inner__section__history__title">
                        <p className="title padding">検索履歴から検索</p>
                      </div>
                      <div className="search-form__inner__section__history__content">
                        <ul className="search-form__inner__section__history__content__list">
                          {searchHistories?.conditions && searchHistories.conditions.length > 0 ? (
                            searchHistories.conditions.map((condition) => (
                              <li
                                className="search-form__inner__section__history__content__list__item"
                                key={condition.id}
                                onClick={() => handleApplyHistoryClick(condition)}
                                style={{ cursor: 'pointer' }}
                              >
                                <p>{condition.body}</p>
                              </li>
                            ))
                          ) : (
                            <li className="search-form__inner__section__history__content__list__item empty">
                              <p>検索履歴がありません</p>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )} */}
              </div>

              <div className="search-form__modal__inner">{renderModalContent()}</div>

              <div className="search-form__modal__submit">
                {modalView === 'main' && (
                  <p className="clear" onClick={handleClearAll}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'employment' && (
                  <p className="clear" onClick={handleClearEmploymentTypes}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'occupation' && (
                  <p className="clear" onClick={handleClearOccupations}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'preference' && (
                  <p className="clear" onClick={handleClearPreferenceConditions}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'salary' && (
                  <p
                    className="clear"
                    onClick={() =>
                      handleChangeSalary?.({
                        target_salary_type: '',
                        target_salary_min: '',
                        target_salary_max: '',
                      })
                    }
                  >
                    すべてクリア
                  </p>
                )}
                {modalView === 'location' && locationView === 'main' && (
                  <p className="clear" onClick={handleClearLocations}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'location' && locationView === 'area' && (
                  <p className="clear" onClick={handleClearAreas}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'location' && locationView === 'prefecture' && (
                  <p className="clear" onClick={handleClearPrefectureDetails}>
                    すべてクリア
                  </p>
                )}
                {modalView === 'save' ? (
                  <button className="submit" onClick={handleSaveConditionClick}>
                    保存する
                  </button>
                ) : modalView === 'main' ? (
                  <button className="submit" onClick={handleSearchClick}>
                    {recruitmentsCount}件を検索する
                  </button>
                ) : (
                  <button className="submit" onClick={handleAddConditionsClick}>
                    追加する
                  </button>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};
